import {
  faBuilding,
  faCheckCircle,
  faChevronDown,
  faExclamationTriangle,
  faFileAlt,
  faSearch,
  faTimes,
  faUpload,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import ServiceCatalogNavigator from 'components/common/ServiceCatalogNavigator';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import ContactRegistrationModal from 'components/modals/ContactRegistrationModal';
import DOMPurify from 'dompurify';
import useApi from 'helpers/api';
import {
  calculatePriority,
  getPriorityProps,
  priorities,
  ticketOrigins
} from 'helpers/utils';
import useDebounce from 'hooks/useDebounce';
import useNotification from 'hooks/useNotification';
import { PrioritySelect } from 'hooks/useTicketsTable';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Form,
  Offcanvas,
  Row
} from 'react-bootstrap';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import CustomerAssetsService from 'services/CustomerAssets.service';
import CustomerContactsService from 'services/CustomerContacts.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import SubcategoriesService from 'services/Subcategories.service';
import TicketsService from 'services/Tickets.service';
import TypeTicketsService from 'services/TypeTickets.service';
import CustomerRegistrationOffcanvas from './CustomerRegistrationOffcanvas';

const TicketRegistrationOffcanvas = ({
  show,
  handleClose,
  preTicketData,
  preTicketId,
  refreshPretickets
}) => {
  const isPreTicketIdEmpty = preTicketId == '';

  const api = useApi();
  const navigate = useNavigate();
  const { showErrorNotification, showSuccessNotification } = useNotification();

  const form = useRef(null);
  const inputCustomer = useRef(null);
  const inputContact = useRef(null);
  const fileInputRef = useRef(null);
  const divOffcanvas = useRef(null);
  const editorRef = useRef(null);

  const [isCreateAndComplete, setIsCreateAndComplete] = useState(false);
  const [showMenuCustomer, setShowMenuCustomers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModalContact, setShowModalContact] = useState(false);
  const [showModalCustomer, setShowModalCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [validated, setValidated] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [searchTermAsset, setSearchTermAsset] = useState('');
  const [showAssetsSuggestions, setShowAssetsSuggestions] = useState(false);
  const [searchTermContact, setSearchTermContact] = useState('');
  const [showContactsSuggestions, setShowContactsSuggestions] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  // const [showServiceNavigator, setShowServiceNavigator] = useState(false); // Removed, showing inline
  const [selectedServicePath, setSelectedServicePath] = useState('');
  const [showServiceCatalogSection, setShowServiceCatalogSection] =
    useState(false);
  const [impact, setImpact] = useState('MEDIUM');
  const [urgency, setUrgency] = useState('MEDIUM');

  // File upload constraints
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 5;
  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ];

  const [filters, setFilters] = useState({
    size: 100,
    includeInactives: false,
    name: ''
  });
  const queryParams = new URLSearchParams(filters).toString();
  const [customers, setCustomers] = useState({ content: [] });
  const [contacts, setContacts] = useState({ content: [] });
  const [typesTicket, setTypesTicket] = useState({ content: [] });
  const [agents, setAgents] = useState([]);
  const [serviceGroups, setServiceGroups] = useState({ content: [] });
  // const [serviceCatalogs, setServiceCatalogs] = useState([]); // No longer used directly
  const [assets, setAssets] = useState([]);

  const [newContact, setNewContact] = useState({});
  const [createdCustomer, setCreatedCustomer] = useState({});
  const [agent, setAgent] = useState({ id: '' });
  const [serviceGroup, setServiceGroup] = useState({ id: '' });
  const [typeTicket, setTypeTicket] = useState({ id: '' });

  const initialState = {
    customer: { id: '' },
    subject: '',
    description: '',
    priority: 'MEDIUM',
    contactCustomer: { id: '' },
    typeService: 'INTERNAL',
    timeSpent: '00:00',
    realized: '',
    isAppointment: false,
    typeViewNote: 'PRIVATE',
    origin: 'INTERNAL'
  };
  const [body, setBody] = useState(initialState);

  useEffect(() => {
    if (show) {
      getTypesTicket();
      getServiceGroups();
      setSelectedCustomer(null);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      setContacts({ content: [] });
      setAgents([]);
      // setServiceCatalogs([]);
      clearFields();
      setIsCreateAndComplete(false);
      setValidated(false);
      setAttachedFiles([]);
      setFileError('');
      setSearchTermAsset('');
      setShowMenuCustomers(false);
      setShowAssetsSuggestions(false);
      setAssets([]);
      setSearchTermContact('');
      setShowContactsSuggestions(false);
      setSelectedContact(null);
      setSelectedServicePath('');
      setShowServiceCatalogSection(false);

      setTimeout(() => {
        if (inputCustomer.current) inputCustomer.current.focus();
      }, 100);
    }
  }, [show]);

  useEffect(() => {
    setBody(preBody => ({
      ...preBody,
      subject: preTicketData?.subject || '',
      description: preTicketData?.description || '',
      customer: {
        id: preTicketData?.customer ? preTicketData.customer.id : ''
      },
      contactCustomer: {
        id:
          preTicketData && preTicketData.contact ? preTicketData.contact.id : ''
      },
      origin: preTicketData?.subject ? 'EMAIL' : 'INTERNAL'
    }));
    setFilters(prevFilters => ({
      ...prevFilters,
      name:
        preTicketData && preTicketData.customer && preTicketData.customer.name
    }));
    if (preTicketData.contact) {
      setSearchTermContact(preTicketData.contact.name || '');
      setSelectedContact(preTicketData.contact);
      if (preTicketData.contact.id == 0) {
        setNewContact(preTicketData.contact);
        const newContent = [...contacts.content];
        newContent.unshift(preTicketData.contact);
        setContacts({ content: newContent });
      }
    }
  }, [preTicketData]);

  useEffect(() => {
    setFilters(prevFilters => ({ ...prevFilters, name: createdCustomer.name }));
    handleSetBody(true, 'customer', createdCustomer.id);
    setSelectedCustomer(createdCustomer);
  }, [createdCustomer]);


  useEffect(() => {
    setContacts({ content: [] });
    setSearchTermContact('');
    setSelectedContact(null);
    setShowContactsSuggestions(false);

    const newBody = { ...body };
    delete newBody.asset;
    setBody(newBody);
    setSearchTermAsset('');
    setAssets([]);
  }, [body.customer.id]);

  useEffect(() => {
    getAgents();
    // getServiceCatalogs(); // No longer needed as we use the navigator
  }, [serviceGroup.id]);


  useEffect(() => {
    getAssets();
  }, [body.customer.id, searchTermAsset]);

  useEffect(() => {
    if (body?.subcategory?.id) {
      getSubcategory();
    }
  }, [body?.subcategory?.id]);

  useEffect(() => {
    if (selectedSubcategory) {
      setBody(prevBody => ({
        ...prevBody,
        subject: selectedSubcategory.defaultSubject || preTicketData?.subject || '',
        description: selectedSubcategory.defaultDescription || preTicketData?.description || '',
        priority: selectedSubcategory.ticketPriority || 'LOW',
        typeTicket: selectedSubcategory.typeTicket
          ? { id: selectedSubcategory.typeTicket.id }
          : null
      }));
      setTypeTicket(
        selectedSubcategory.typeTicket
          ? { id: selectedSubcategory.typeTicket.id }
          : { id: '' }
      );
      // Set impact and urgency from subcategory if available
      if (selectedSubcategory.impact) {
        setImpact(selectedSubcategory.impact || 'MEDIUM');
      }
      if (selectedSubcategory.urgency) {
        setUrgency(selectedSubcategory.urgency || 'MEDIUM');
      }
    }
  }, [selectedSubcategory]);

  // Calculate priority based on impact and urgency or set PLANNED
  useEffect(() => {
    if (impact && urgency) {
      const calculatedPriority = calculatePriority(urgency, impact);
      if (calculatedPriority) {
        handleSetBody(false, 'priority', calculatedPriority);
      }
    }
  }, [impact, urgency]);

  const getSubcategory = async () => {
    try {
      const response = await SubcategoriesService.findById(body.subcategory.id);
      if (response.id) setSelectedSubcategory(response);
    } catch (error) {
      console.error('Erro ao buscar subcategoria', error);
    }
  };

  // File handling functions
  const handleFileSelect = event => {
    const files = Array.from(event.target.files);
    setFileError('');

    if (attachedFiles.length + files.length > MAX_FILES) {
      setFileError(`Máximo de ${MAX_FILES} arquivos permitidos`);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: arquivo muito grande (máximo 10MB)`);
        return;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: tipo de arquivo não permitido`);
        return;
      }

      // Check for duplicate names
      if (attachedFiles.some(f => f.name === file.name)) {
        errors.push(`${file.name}: arquivo já anexado`);
        return;
      }

      validFiles.push({
        file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type
      });
    });

    if (errors.length > 0) {
      setFileError(errors.join(', '));
    }

    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = fileId => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
    setFileError('');
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Create FormData with ticket data and files
  const createFormData = () => {
    const formData = new FormData();

    // Add ticket data as JSON
    const ticketData = { ...body };
    formData.append(
      'ticket',
      new Blob([JSON.stringify(ticketData)], { type: 'application/json' })
    );

    // Add files
    attachedFiles.forEach((fileObj, index) => {
      formData.append(`files`, fileObj.file);
    });

    return formData;
  };

  const getCustomers = async () => {
    if (filters.name != '') {
      const params = new URLSearchParams(filters).toString();
      let response = await api.customers.list(params);
      if (response.content) setCustomers(response);
    }
  };

  const getContacts = async () => {
    // Don't get contacts if customer is not selected or search term is empty
    if (!body.customer.id) return;
    if (searchTermContact.length === 0) return;

    // Get contacts
    const searchParams = new URLSearchParams({
      searchTerm: searchTermContact
    }).toString();
    const response = await CustomerContactsService.searchByNameAndEmail(
      body.customer.id,
      searchParams
    );

    // If response is not an array, show error and reset contacts
    if (!Array.isArray(response)) {
      showErrorNotification(response.userMessage);
      resetContacts();
      return;
    }

    // Create a new array with the response
    const contacts = [...response];

    // If preTicketData has a contact and the contact id is 0, add the contact to the array and set the selected contact
    if (preTicketData && preTicketData.contact?.id === 0) {
      contacts.unshift(preTicketData.contact);
      setSelectedContact(preTicketData.contact);
      setSearchTermContact(preTicketData.contact.name);
    }

    // Set the contacts
    setContacts({ content: contacts });
  };

  const resetContacts = () => {
    setContacts({ content: [] });
    setSelectedContact(null);
    setSearchTermContact('');
  };

  const getTypesTicket = async () => {
    let response = await TypeTicketsService.list(queryParams);
    if (response.content) setTypesTicket(response);
  };

  const getServiceGroups = async () => {
    let response = await ServiceGroupService.list(queryParams);
    if (response.content) setServiceGroups(response);
  };

  const getAgents = async () => {
    if (serviceGroup.id) {
      let response = await api.serviceGroupsAgents.list(serviceGroup.id);
      setAgents(response);
    }
  };

  const getAssets = async () => {
    if (!body.customer.id) return;

    const queryParam = new URLSearchParams({
      searchTerm: searchTermAsset
    }).toString();
    const response = await CustomerAssetsService.list(
      body.customer.id,
      queryParam
    );
    if (response && Array.isArray(response)) {
      setAssets(response);
    }
  };

  const debouncedGetCustomers = useDebounce(name => {
    if (!name) return;
    const params = new URLSearchParams({ ...filters, name }).toString();
    api.customers.list(params).then(r => { if (r.content) setCustomers(r); });
  }, 500);

  const debouncedGetContacts = useDebounce(term => {
    if (!body.customer.id || !term) return;
    const searchParams = new URLSearchParams({ searchTerm: term }).toString();
    CustomerContactsService.searchByNameAndEmail(body.customer.id, searchParams)
      .then(response => {
        if (!Array.isArray(response)) {
          showErrorNotification(response.userMessage);
          resetContacts();
          return;
        }
        const newContacts = [...response];
        if (preTicketData?.contact?.id === 0) {
          newContacts.unshift(preTicketData.contact);
          setSelectedContact(preTicketData.contact);
          setSearchTermContact(preTicketData.contact.name);
        }
        setContacts({ content: newContacts });
      });
  }, 500);

  const handleSetBody = (hasId, fieldName, value) => {
    hasId
      ? setBody(prevBody => ({
          ...prevBody,
          [fieldName]: { id: parseInt(value) }
        }))
      : setBody(prevBody => ({ ...prevBody, [fieldName]: value }));
  };

  const onSubmit = async e => {
    console.log(body);
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.description.trim() === '' ||
      body.description === '<p><br></p>' ||
      !impact ||
      !urgency ||
      !body.priority
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      let response;
      const formData = createFormData();

      if (isPreTicketIdEmpty) {
        response = await TicketsService.create(formData);
      } else {
        response = await TicketsService.createFromPreticket(
          preTicketId,
          formData
        );
        refreshPretickets();
      }

      if (response.id) {
        showSuccessNotification('Ticket criado com sucesso');
        setValidated(false);
        setIsCreateAndComplete(false);
        clearFields();
        handleClose();
      } else {
        showErrorNotification(response);
      }
    }
    setLoading(false);
  };

  const createAndOpen = async e => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.description.trim() === '' ||
      body.description === '<p><br></p>' ||
      !impact ||
      !urgency ||
      !body.priority
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      let response;
      const formData = createFormData();

      if (isPreTicketIdEmpty) {
        response = await TicketsService.create(formData);
      } else {
        response = await TicketsService.createFromPreticket(
          preTicketId,
          formData
        );
      }

      if (response.id) {
        showSuccessNotification('Ticket criado com sucesso');
        setValidated(false);
        setIsCreateAndComplete(false);
        clearFields();
        handleClose();
        navigate(`/tickets/${response.id}`);
      } else {
        showErrorNotification(response);
      }
    }
    setLoading(false);
  };

  const createAndComplete = async e => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.description.trim() === '' ||
      body.description === '<p><br></p>' ||
      body.realized.trim() === '' ||
      body.realized === '<p><br></p>' ||
      !impact ||
      !urgency ||
      !body.priority
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      let response;
      const formData = createFormData();

      if (isPreTicketIdEmpty) {
        response = await TicketsService.createAndComplete(formData);
      } else {
        response = await TicketsService.createFromPreticketAndComplete(
          preTicketId,
          formData
        );
      }

      if (response.id) {
        showSuccessNotification('Ticket criado com sucesso');
        setValidated(false);
        setIsCreateAndComplete(false);
        clearFields();
        handleClose();
        navigate(`/tickets/${response.id}`);
      } else {
        showErrorNotification(response);
      }
    }
    setLoading(false);
  };

  const clearFields = () => {
    setFilters(prevFilters => ({ ...prevFilters, name: '' }));
    setBody(initialState);
    setServiceGroup({ id: '' });
    setAgent({ id: '' });
    setTypeTicket({ id: '' });
    setAttachedFiles([]);
    setFileError('');
    setSearchTermContact('');
    setSelectedContact(null);
    setSelectedServicePath('');
    setShowServiceCatalogSection(false);
    setImpact('MEDIUM');
    setUrgency('MEDIUM');
  };

  const handleServiceNavigatorSelect = (item, history) => {
    // Handle subcategory - it can be nested in item.subcategory (from search) or item itself (from navigation)
    const subcategory = item.subcategory || item;
    const subcategoryId = subcategory.id || item.id;
    const subcategoryName = subcategory.name || item.name;

    handleSetBody(true, 'subcategory', subcategoryId);

    // Set selectedSubcategory if we have the full subcategory object (from search)
    // This will trigger the useEffect that processes defaultSubject, defaultDescription, etc.
    if (item.subcategory) {
      setSelectedSubcategory(item.subcategory);
    }

    // Use history if available, otherwise try to build from DTO fields
    let serviceGroup, catalog, category;
    let servicePath;

    if (history && history.length > 0) {
      // Assuming history is [Group, Catalog, Category]
      serviceGroup = history[0];
      catalog = history[1];
      category = history[2];
    } else if (
      item.serviceGroupId &&
      item.serviceGroupName &&
      item.catalogId &&
      item.catalogName &&
      item.categoryId &&
      item.categoryName
    ) {
      // Build from DTO fields when history is not available (search results)
      serviceGroup = {
        id: item.serviceGroupId,
        name: item.serviceGroupName
      };
      catalog = {
        id: item.catalogId,
        name: item.catalogName
      };
      category = {
        id: item.categoryId,
        name: item.categoryName
      };
    }

    // Set service group, catalog, and category if available
    if (serviceGroup) {
      setServiceGroup({ id: serviceGroup.id });
      handleSetBody(true, 'serviceGroup', serviceGroup.id);
    }

    if (catalog) {
      handleSetBody(true, 'serviceCatalog', catalog.id);
    }

    if (category) {
      handleSetBody(true, 'category', category.id);
    }

    // Build service path including subcategory
    if (serviceGroup) {
      servicePath = `${serviceGroup.name}${
        catalog ? ' > ' + catalog.name : ''
      }${category ? ' > ' + category.name : ''}${
        subcategoryName ? ' > ' + subcategoryName : ''
      }`;
    } else {
      // Fallback for search or missing history
      servicePath = subcategoryName || 'Serviço selecionado';
    }

    setSelectedServicePath(servicePath);

    // setShowServiceNavigator(false); // No longer modal
    setShowServiceCatalogSection(false); // Collapse after selection
  };

  // Handle asset selection
  const handleAssetSelect = asset => {
    handleSetBody(true, 'asset', asset.id);
    setSearchTermAsset(asset.name);
    setShowAssetsSuggestions(false);
    setAssets([]);
  };

  const handleCustomerSelect = customer => {
    setFilters(prevFilters => ({
      ...prevFilters,
      name: customer.fantasyName || customer.name
    }));
    handleSetBody(true, 'customer', customer.id);
    setSelectedCustomer(customer);
    inputContact.current.focus();
    setShowMenuCustomers(false);
  };

  const handleContactSelect = contact => {
    setSearchTermContact(contact.name);
    handleSetBody(true, 'contactCustomer', contact.id);
    setSelectedContact(contact);
    setShowContactsSuggestions(false);
  };

  const handleSearchTermChange = e => {
    const value = e.target.value;
    setSearchTermAsset(value);
    setShowAssetsSuggestions(value.length > 0);
  };

  return (
    <>
      {loading && <Loading />}
      <ContactRegistrationModal
        showModal={showModalContact}
        handleClose={() => setShowModalContact(false)}
        customer={selectedCustomer}
        newContact={response => {
          setSearchTermContact(response.name);
          setSelectedContact(response);
          handleSetBody(true, 'contactCustomer', response.id);
        }}
        isSuccess={response => {
          if (response) {
            setSearchTermContact(response.name);
            setSelectedContact(response);
          }
        }}
      />
      <CustomerRegistrationOffcanvas
        showModal={showModalCustomer}
        handleClose={() => setShowModalCustomer(false)}
        createdCustomer={item => setCreatedCustomer(item)}
        isSuccess={response => {}}
      />

      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        style={{ minWidth: '65%', maxWidth: '95%' }}
        ref={divOffcanvas}
      >
        <Offcanvas.Header
          tabIndex={-1}
          className="d-flex align-items-center space-between"
        >
          <Offcanvas.Title className="m-0">Novo ticket</Offcanvas.Title>
          <span role="button" onClick={() => handleClose()} tabIndex={-1}>
            <FontAwesomeIcon icon={faTimes} />
          </span>
        </Offcanvas.Header>
        <Offcanvas.Body className="pb-0">
          <Form
            ref={form}
            validated={validated}
            style={{ paddingBottom: '60px' }}
          >
            <Row>
              {/* Left Column - Mandatory Fields */}
              <Col md={8}>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="exampleForm.customer">
                      <Form.Label className="mb-1 fs-10">
                        Cliente <span className="text-danger">*</span>
                      </Form.Label>
                      <SearchInputWithSuggestions
                        searchTerm={filters.name}
                        placeholder="Digite para pesquisar..."
                        ref={inputCustomer}
                        suggestions={customers.content}
                        showSuggestions={showMenuCustomer}
                        onSearchChange={e => {
                          const value = e.target.value;
                          setFilters({ ...filters, name: value });
                          setShowMenuCustomers(value.length > 0);
                          debouncedGetCustomers(value);
                        }}
                        onSuggestionSelect={handleCustomerSelect}
                        renderSuggestionItem={customer => (
                          <div
                            key={customer.id}
                            className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                            onClick={() => handleCustomerSelect(customer)}
                            role="button"
                            style={{
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <div className="fw-bold">{customer.name}</div>
                            <div className="text-muted small">
                              {customer.type === 'JURIDICAL_PERSON' ? (
                                <>
                                  <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="me-1"
                                  />{' '}
                                  Pessoa Jurídica
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon
                                    icon={faUser}
                                    className="me-1"
                                  />{' '}
                                  Pessoa Física
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      />
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        Selecione um cliente.
                      </Form.Control.Feedback>

                      <div className="w-100 d-flex justify-content-end">
                        <Button
                          variant="link"
                          onClick={() => setShowModalCustomer(true)}
                        >
                          Adicionar novo cliente
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="mb-1 fs-10">
                        Contato <span className="text-danger">*</span>
                      </Form.Label>
                      <SearchInputWithSuggestions
                        searchTerm={searchTermContact}
                        placeholder="Digite para pesquisar..."
                        ref={inputContact}
                        disabled={!body.customer.id}
                        required={true}
                        suggestions={contacts.content}
                        showSuggestions={showContactsSuggestions}
                        onSearchChange={e => {
                          const value = e.target.value;
                          setSearchTermContact(value);
                          setShowContactsSuggestions(value.length > 0);
                          debouncedGetContacts(value);
                        }}
                        onSuggestionSelect={handleContactSelect}
                        renderSuggestionItem={contact => (
                          <div
                            key={contact.id}
                            className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                            onClick={() => handleContactSelect(contact)}
                            role="button"
                            style={{
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <div className="fw-bold">{contact.name}</div>
                            <div className="text-muted small">
                              {contact.email}
                            </div>
                          </div>
                        )}
                      />
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        Selecione um contato.
                      </Form.Control.Feedback>

                      <div className="w-100 d-flex justify-content-end mt-1">
                        <Button
                          variant="link"
                          onClick={() => setShowModalContact(true)}
                          disabled={body.customer.id === ''}
                        >
                          Adicionar novo contato
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {selectedCustomer && selectedCustomer.observation && (
                  <Row>
                    <Col md={12}>
                      <Alert variant="warning">
                        <Flex alignItems="center" className="mb-2">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          <span className="ms-2 fw-medium">
                            Observação do cliente
                          </span>
                        </Flex>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              selectedCustomer.observation
                            )
                          }}
                        />
                      </Alert>
                    </Col>
                  </Row>
                )}

                {/* Service Catalog Navigator Inline */}
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Label className="mb-1 fs-10">
                      Serviço (Catálogo)
                      {isCreateAndComplete && (
                        <span className="text-danger"> *</span>
                      )}
                    </Form.Label>

                    {!showServiceCatalogSection && (
                      <div
                        className="d-grid gap-2 mb-3"
                        style={{ maxWidth: '100%' }}
                      >
                        {' '}
                        {/* Garante que o grid não estoure */}
                        <div
                          onClick={() => setShowServiceCatalogSection(true)}
                          className="text-start border border-1 border-200 rounded p-2"
                          style={{
                            borderStyle: 'dashed',
                            cursor: 'pointer',
                            display: 'flex', // Garante comportamento flex direto no container
                            alignItems: 'center', // Alinha verticalmente
                            maxWidth: '100%', // A CHAVE: impede que o input cresça além da coluna pai
                            paddingRight: '8px' // Ajuste fino para o ícone não colar na borda
                          }}
                        >
                          <Flex
                            justifyContent="between"
                            alignItems="center"
                            className="w-100"
                            style={{ overflow: 'hidden' }} // Contenção extra
                          >
                            <span
                              className={`text-truncate ${
                                selectedServicePath
                                  ? 'fw-semibold'
                                  : 'text-muted'
                              }`}
                              style={{
                                flex: '1',
                                minWidth: 0,
                                display: 'block'
                              }}
                              title={
                                selectedServicePath ||
                                'Clique para pesquisar por um serviço...'
                              }
                            >
                              {selectedServicePath ||
                                'Clique para pesquisar por um serviço...'}
                            </span>

                            <FontAwesomeIcon
                              icon={faSearch}
                              className="ms-2 flex-shrink-0"
                            />
                          </Flex>
                        </div>
                      </div>
                    )}

                    {showServiceCatalogSection && (
                      <div className="border rounded p-3 bg-100 mb-3">
                        <div className="d-flex justify-content-end mb-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-secondary p-0"
                            onClick={() => setShowServiceCatalogSection(false)}
                          >
                            <FontAwesomeIcon icon={faTimes} /> Fechar
                          </Button>
                        </div>
                        <ServiceCatalogNavigator
                          onSelect={handleServiceNavigatorSelect}
                        />
                      </div>
                    )}

                    <Form.Control
                      type="text"
                      className="d-none"
                      required={isCreateAndComplete}
                      value={body?.subcategory?.id || ''}
                      onChange={() => {}} // Read-only
                    />
                    <Form.Control.Feedback type="invalid">
                      Selecione um serviço.
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {/* Calculated Priority Display */}
                {(() => {
                  const displayPriority =
                    impact && urgency
                      ? calculatePriority(urgency, impact)
                      : null;

                  if (!displayPriority) return null;

                  const priorityProps = getPriorityProps(displayPriority);
                  const priorityInfo = priorities.find(
                    p => p.value === displayPriority
                  );

                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="between"
                      className="mt-3"
                    >
                      <Form.Label className="fs-9 fw-semibold text-900 mb-2">
                        Prioridade Calculada (ITIL)
                      </Form.Label>
                      <div className="d-flex justify-content-center">
                        <PrioritySelect
                          title={priorityInfo?.name || priorityProps.text}
                          color={priorityProps.bg}
                          data={priorityProps.percentage}
                        />
                      </div>
                    </Flex>
                  );
                })()}

                {/* Priority Calculation Component */}
                <Form.Group className="mb-3">
                  <Row className="g-3 mb-3">
                    {/* Impact Column */}
                    <Col md={6}>
                      <div className="mb-2">
                        <Form.Label className="fs-9 fw-semibold text-900 mb-2">
                          Impacto
                        </Form.Label>
                        <div className="d-flex flex-column gap-2">
                          {[
                            {
                              value: 'HIGH',
                              label: 'Alto',
                              description:
                                'Afeta toda a empresa ou serviços críticos.'
                            },
                            {
                              value: 'MEDIUM',
                              label: 'Médio',
                              description:
                                'Afeta um departamento ou grupo parcial.'
                            },
                            {
                              value: 'LOW',
                              label: 'Baixo',
                              description:
                                'Afeta um único usuário ou tem solução.'
                            }
                          ].map(option => (
                            <div
                              key={option.value}
                              onClick={() => setImpact(option.value)}
                              className={`border rounded p-2 transition-all ${
                                impact === option.value
                                  ? 'border-success border-2'
                                  : 'border-200'
                              }`}
                              style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div className="d-flex align-items-start gap-2">
                                <div className="mt-1">
                                  {impact === option.value ? (
                                    <FontAwesomeIcon
                                      icon={faCheckCircle}
                                      className="text-success"
                                    />
                                  ) : (
                                    <div
                                      className="rounded-circle border border-300"
                                      style={{
                                        width: '20px',
                                        height: '20px'
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="fw-semibold text-900 mb-1">
                                    {option.label}
                                  </div>
                                  <div className="text-muted small">
                                    {option.description}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>

                    {/* Urgency Column */}
                    <Col md={6}>
                      <div className="mb-2">
                        <Form.Label className="fs-9 fw-semibold text-900 mb-2">
                          Urgência
                        </Form.Label>
                        <div className="d-flex flex-column gap-2">
                          {[
                            {
                              value: 'HIGH',
                              label: 'Alta',
                              description:
                                'Sistema parado, necessita ação imediata.'
                            },
                            {
                              value: 'MEDIUM',
                              label: 'Média',
                              description:
                                'Funcionalidade degradada, mas operante.'
                            },
                            {
                              value: 'LOW',
                              label: 'Baixa',
                              description: 'Dúvidas, solicitações ou melhorias.'
                            }
                          ].map(option => (
                            <div
                              key={option.value}
                              onClick={() => setUrgency(option.value)}
                              className={`border rounded p-2 transition-all ${
                                urgency === option.value
                                  ? 'border-success border-2'
                                  : 'border-200'
                              }`}
                              style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div className="d-flex align-items-start gap-2">
                                <div className="mt-1">
                                  {urgency === option.value ? (
                                    <FontAwesomeIcon
                                      icon={faCheckCircle}
                                      className="text-success"
                                    />
                                  ) : (
                                    <div
                                      className="rounded-circle border border-300"
                                      style={{
                                        width: '20px',
                                        height: '20px'
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="fw-semibold text-900 mb-1">
                                    {option.label}
                                  </div>
                                  <div className="text-muted small">
                                    {option.description}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Hidden input for form validation */}
                  <Form.Control
                    type="text"
                    className="d-none"
                    required
                    value={body.priority || ''}
                    onChange={() => {}}
                  />
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Selecione o impacto e a urgência para calcular a prioridade.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label className="mb-1 fs-10">
                    Assunto <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Assunto"
                    autocomplete="off"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    value={body.subject}
                    required
                    onChange={e =>
                      handleSetBody(false, 'subject', e.target.value)
                    }
                  />
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Insira um assunto
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label className="mb-1 fs-10 d-flex align-items-center justify-content-between">
                    <span>
                      Descrição <span className="text-danger"> *</span>
                    </span>
                    <MessageTemplateSelector
                      position="start"
                      type="TICKET_DESCRIPTION"
                      editorRef={editorRef}
                      onTemplateApplied={content => {
                        handleSetBody(false, 'description', content);
                      }}
                    />
                  </Form.Label>
                  <div className="position-relative">
                    <Editor
                      ref={editorRef}
                      data={body.description}
                      value={value =>
                        handleSetBody(false, 'description', value)
                      }
                    />
                    <MessageTemplateAutocomplete
                      editorRef={editorRef}
                      type="TICKET_DESCRIPTION"
                      onTemplateApplied={content => {
                        handleSetBody(false, 'description', content);
                      }}
                    />
                  </div>

                  {validated && (body.description.trim() === '' || body.description === '<p><br></p>') && (
                    <span className="d-block text-danger fs-10 mt-1">
                      Campo obrigatório.
                    </span>
                  )}
                </Form.Group>

                <div
                  className={
                    isCreateAndComplete ? 'd-block' : 'visually-hidden'
                  }
                >
                  <Row className="mb-3 d-flex">
                    <Form.Group as={Col} md="12" className="mb-3">
                      <Form.Label>Apontar horas</Form.Label>
                      <Form.Switch
                        value={body.isAppointment}
                        onChange={() =>
                          handleSetBody(
                            false,
                            'isAppointment',
                            !body.isAppointment
                          )
                        }
                      />
                    </Form.Group>
                    {!body.isAppointment ? (
                      <>
                        <Form.Group as={Col} md="12">
                          <Form.Label>Visualização</Form.Label>
                          <Form.Select
                            aria-label="Tipo"
                            className="flex-1 me-2"
                            value={body.typeViewNote}
                            onChange={e =>
                              handleSetBody(
                                false,
                                'typeViewNote',
                                e.target.value
                              )
                            }
                            required={isCreateAndComplete}
                          >
                            <option value="PRIVATE" key="PRIVATE">
                              Privado
                            </option>
                            <option value="PUBLIC" key="PUBLIC">
                              Público
                            </option>
                          </Form.Select>
                          <Form.Control.Feedback>
                            Muito bom!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            Escolha uma opção!
                          </Form.Control.Feedback>
                        </Form.Group>
                      </>
                    ) : (
                      <>
                        <Form.Group
                          as={Col}
                          md="6"
                          controlId="appointment.type"
                        >
                          <Form.Label>
                            Tipo de horas
                            {isCreateAndComplete && (
                              <span className="text-danger"> *</span>
                            )}
                          </Form.Label>
                          <Form.Select
                            aria-label="Tipo"
                            className="flex-1 me-2"
                            value={body.typeService}
                            onChange={e =>
                              handleSetBody(
                                false,
                                'typeService',
                                e.target.value
                              )
                            }
                            required={body.isAppointment}
                          >
                            <option value="INTERNAL" key="INTERNAL">
                              Interno
                            </option>
                            <option value="EXTERNAL" key="EXTERNAL">
                              Externo
                            </option>
                          </Form.Select>
                          <Form.Control.Feedback>
                            Muito bom!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            Escolha uma opção!
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                          as={Col}
                          md="6"
                          controlId="appointment.profile"
                        >
                          <Form.Label>
                            Horas
                            {isCreateAndComplete && (
                              <span className="text-danger"> *</span>
                            )}
                          </Form.Label>

                          <MaskedInput
                            mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                            placeholder="HH:MM"
                            required
                            guide={false}
                            value={body.timeSpent}
                            onChange={e =>
                              handleSetBody(false, 'timeSpent', e.target.value)
                            }
                            render={(ref, props) => (
                              <Form.Control ref={ref} {...props} />
                            )}
                          />
                          <Form.Control.Feedback>
                            Muito bom!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            Insira as horas trabalhadas!
                          </Form.Control.Feedback>
                        </Form.Group>
                      </>
                    )}
                  </Row>
                  <Form.Group className="mb-3" controlId="appointment.profile">
                    <Form.Label>
                      Realizado
                      {isCreateAndComplete && (
                        <span className="text-danger"> *</span>
                      )}
                    </Form.Label>
                    <Editor
                      value={value => handleSetBody(false, 'realized', value)}
                    />

                      {validated && isCreateAndComplete && (body.realized.trim() === '' || body.realized === '<p><br></p>') && (
                        <span className="d-block text-danger fs-10 mt-1">
                          Campo obrigatório.
                        </span>
                      )}
                  </Form.Group>
                </div>
              </Col>

              {/* Right Column - Ticket Properties */}
              <Col md={4}>
                <h5 className="mb-3">Propriedades do ticket</h5>

                {/*
                  Removed the "Serviço (Catálogo)" section from here
                  as it was moved to the left column (main area)
                  according to the user request.
                */}

                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label className="mb-1 mt-2 fs-10">
                    Agente
                    {isCreateAndComplete && (
                      <span className="text-danger"> *</span>
                    )}
                  </Form.Label>
                  <Form.Select
                    value={agent.id}
                    required={isCreateAndComplete}
                    disabled={!serviceGroup.id}
                    onChange={e => {
                      setAgent({ id: e.target.value });
                      handleSetBody(true, 'agent', e.target.value);
                    }}
                  >
                    <option value="" disabled>
                      {serviceGroup.id
                        ? 'Selecione uma opção'
                        : 'Selecione um serviço primeiro'}
                    </option>
                    {agents.map(item => (
                      <option value={item.id} key={item.phone + item.firstName}>
                        {item.firstName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Selecione um agente.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  className="mb-3"
                  controlId="exampleForm.ControlInput1"
                >
                  <Form.Label className="mb-1 fs-10">
                    Tipo
                    {isCreateAndComplete && (
                      <span className="text-danger"> *</span>
                    )}
                  </Form.Label>
                  <Form.Select
                    value={typeTicket?.id}
                    required={isCreateAndComplete}
                    onChange={e => {
                      setTypeTicket({ id: e.target.value });
                      handleSetBody(true, 'typeTicket', e.target.value);
                    }}
                  >
                    <option value="" disabled>
                      Selecione uma opção
                    </option>
                    {typesTicket.content.map(item => (
                      <option value={item.id} key={item.id + item.name}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Selecione um tipo.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 fs-10">Ativo</Form.Label>

                  <SearchInputWithSuggestions
                    searchTerm={searchTermAsset}
                    placeholder="Digite o nome ou o usuário do ativo..."
                    disabled={!body.customer.id}
                    suggestions={assets}
                    showSuggestions={showAssetsSuggestions}
                    onSearchChange={handleSearchTermChange}
                    onSuggestionSelect={handleAssetSelect}
                    renderSuggestionItem={asset => (
                      <div
                        key={asset.id}
                        className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                        onClick={() => handleAssetSelect(asset)}
                        role="button"
                        style={{
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <div className="fw-bold">{asset.name}</div>
                        <div className="text-muted small">
                          {asset.dattoDevice
                            ? asset.dattoDevice.lastLoggedUser
                            : asset.assetType.name}
                        </div>
                      </div>
                    )}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="mb-1 fs-10">
                    Origem do ticket
                  </Form.Label>
                  <Form.Select
                    value={body.origin}
                    onChange={e =>
                      handleSetBody(false, 'origin', e.target.value)
                    }
                  >
                    {Object.entries(ticketOrigins).map(([key, value]) => (
                      <option value={key} key={key}>
                        {value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="rounded p-4 mb-4">
                  <Form.Label className="fs-8 fw-medium mb-2">
                    Anexos
                  </Form.Label>
                  <p className="text-muted small mb-3">
                    Adicione arquivos que possam ajudar no atendimento da sua
                    solicitação
                  </p>

                  <div
                    className="border border-2 border-dashed rounded text-center p-4 mb-3"
                    style={{ borderColor: '#dee2e6' }}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      className="text-muted mb-3"
                      style={{ fontSize: '1.5rem' }}
                    />
                    <p className="mb-1 fw-medium small">
                      Clique para adicionar arquivos ou arraste aqui
                    </p>
                    <p className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Formatos aceitos: PDF, DOC, DOCX, CSV, JPG, PNG, GIF, TXT,
                      XML, JSON, ZIP – até 6MB por arquivo
                    </p>

                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.xml,.json,.pptx,.ppt,.pfx"
                    />

                    <Button
                      variant="falcon-default"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-2"
                    >
                      Selecionar Arquivos
                    </Button>
                  </div>

                  {fileError && (
                    <Alert variant="danger" className="py-2 small">
                      {fileError}
                    </Alert>
                  )}

                  {attachedFiles.length > 0 && (
                    <div className="mt-3">
                      {attachedFiles.map(fileObj => (
                        <div
                          key={fileObj.id}
                          className="bg-100 d-flex align-items-center gap-2 p-2 mb-2 rounded"
                        >
                          <FontAwesomeIcon
                            icon={faFileAlt}
                            className="text-muted me-2"
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold small">
                              {fileObj.name}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {formatFileSize(fileObj.size)}
                            </div>
                          </div>
                          <Button
                            variant="link"
                            className="text-secondary"
                            size="sm"
                            onClick={() => removeFile(fileObj.id)}
                            style={{ padding: '0.25rem 0.5rem' }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <footer
            className="bg-100 p-2 mt-3 d-flex justify-content-end"
            style={{
              width: '55%',
              height: '50px',
              position: 'fixed',
              right: 0,
              bottom: 0
            }}
          >
            <Button
              variant="falcon-default"
              onClick={handleClose}
              className="me-2"
            >
              Cancelar
            </Button>
            {!isCreateAndComplete ? (
              <div className="position-relative d-flex">
                <Button
                  style={{ borderRadius: '40px 0 0 40px' }}
                  variant="primary"
                  type="submit"
                  onClick={onSubmit}
                >
                  Salvar
                </Button>
                <Dropdown drop="up" className="d-flex h-100">
                  <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>

                  <Dropdown.Menu
                    className="position-absolute w-100"
                    style={{ transform: 'translateX(-65%)' }}
                  >
                    <Dropdown.Item onClick={createAndOpen}>
                      Salvar e visualizar
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setIsCreateAndComplete(true)}>
                      Salvar e concluir
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ) : (
              <Button
                variant="primary"
                type="submit"
                onClick={createAndComplete}
              >
                Salvar
              </Button>
            )}
          </footer>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

const CustomToggle = React.forwardRef(({ onClick }, ref) => (
  <Button
    style={{ borderRadius: '0 5px 5px 0' }}
    ref={ref}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default TicketRegistrationOffcanvas;
